import React, { useCallback, useMemo, useState } from "react";
import "./BudgetIntakeToolSettings.scss";
import { RouteComponentProps } from "@reach/router";
import {
  Button,
  ButtonFrameworkVariant,
  ButtonType,
  Dropdown,
  DropdownToggleType,
  Spinner,
} from "../Components";
import { MdDelete, MdSave } from "react-icons/md";
import { ToolsLambdaFetch } from "../utils/fetch-utils";
import { useSetError } from "../redux/modals";

interface BudgetIntakeToolGoalsProps {
  company: string;
  originalMediaTypesActive: Map<string, boolean>;
  mediaTypesActive: Map<string, boolean>;
  setMediaTypesActive: React.Dispatch<React.SetStateAction<Map<string, boolean>>>;
  setOriginalMediaTypesActive: React.Dispatch<React.SetStateAction<Map<string, boolean>>>;
  handleMediaTypeActiveChange: (media_type: string, value: string) => void;
}

const BudgetIntakeToolSettings = ({
  company,
  originalMediaTypesActive,
  mediaTypesActive,
  setMediaTypesActive,
  setOriginalMediaTypesActive,
  handleMediaTypeActiveChange,
}: BudgetIntakeToolGoalsProps & RouteComponentProps): JSX.Element => {
  const setError = useSetError();
  const [activeAPICall, setActiveAPICall] = useState(false);

  const hasChanges = useMemo(() => {
    if (mediaTypesActive.size !== originalMediaTypesActive.size) {
      return true;
    }

    for (const [key, value] of Array.from(mediaTypesActive.entries())) {
      if (originalMediaTypesActive.get(key) !== value) {
        return true;
      }
    }
    return false;
  }, [mediaTypesActive, originalMediaTypesActive]);

  const saveGoalGranularitySettings = useCallback(async () => {
    try {
      setActiveAPICall(true);
      const mediaTypeSettings = Array.from(mediaTypesActive.entries()).map(
        ([mediaType, isMediaTypeActive]) => ({
          mediaType,
          isMediaTypeActive,
        })
      );

      await ToolsLambdaFetch("/setCampaignTypes", {
        method: "POST",
        body: JSON.stringify({
          company,
          mediaTypeSettings,
        }),
      });

      setOriginalMediaTypesActive(new Map(mediaTypesActive));
    } catch (e) {
      setError({
        message: e instanceof Error ? e.message : String(e),
        reportError: e,
      });
    }
    setActiveAPICall(false);
  }, [company, mediaTypesActive, setError, setOriginalMediaTypesActive]);

  return (
    <div className="budgetIntakeToolSettings">
      <div className="budgetIntakeToolSettingsHeaderContainer">
        <div className="budgetIntakeToolSettingsHeaderTextContainer">
          <div className="budgetIntakeToolSettingsHeaderTextColumn">
            <div className="budgetIntakeToolSettingsHeaderText">Goal Granularity Settings</div>
            <div className="bugdetIntakeToolSettingsExplainerText">Goal Granularity Settings</div>
          </div>
        </div>
      </div>
      <div className="budgetIntakeToolSettingsLineBelowHeader"></div>
      <div className="budgetIntakeToolSettingsBodyContainer">
        <div className="budgetIntakeToolSettingsContainer">
          <div className="budgetIntakeToolSettingsTextAndDropDownContainer">
            {Array.from(mediaTypesActive.entries()).map(([item, isSelected], index) => {
              return (
                <div className="budgetIntakeToolSettingsRow" key={index}>
                  <div className="budgetIntakeToolSettingsRowHeaderContainer">
                    <div className="budgetIntakeToolSettingsHeaderText">{item}</div>
                  </div>
                  <Dropdown
                    type={DropdownToggleType.OUTLINED}
                    onChange={option => handleMediaTypeActiveChange(item, option)}
                    options={[
                      { label: "Campaign and Segment Level", value: "campaign" },
                      { label: "Segment Level", value: "segment" },
                    ]}
                    value={mediaTypesActive.get(item) ? "campaign" : "segment"}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="budgetIntakeToolSettingsFooterContainer">
        <div className="budgetIntakeToolSettingsFooterButtons">
          <Button
            type={ButtonType.OUTLINED}
            variant={ButtonFrameworkVariant.LEADING_ICON}
            icon={<MdDelete style={{ color: "#8254FF" }} />}
            onClick={() => setMediaTypesActive(new Map(originalMediaTypesActive))}
            disabled={!hasChanges}
          >
            Discard Edits
          </Button>
          <Button
            type={ButtonType.FILLED}
            background="light"
            variant={ButtonFrameworkVariant.LEADING_ICON}
            icon={<MdSave />}
            onClick={saveGoalGranularitySettings}
            disabled={!hasChanges}
          >
            {activeAPICall ? <Spinner /> : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BudgetIntakeToolSettings;
